import "../../payment-methods/credit-card/credit-card-payment-method"
import "../../payment-methods/paypal/paypal-payment-method";
import "../../payment-methods/po-order/po-order-payment-method";
import FreeTrialCountryRestriction from '../../free-trial/utils/free-trial-country-restriction.util';
import store from "../../../store/store";
import { ActionStatus } from '../../../common/constants/action-status';
import { BraintreeHostedFieldsStatus, ClientTokenDetails } from "../shop-checkout.interface";
import { MessageTemplateId } from '../../../common/constants/api-constants';
import { PaymentBillingCheckoutStepBase } from "../payment-billing-checkout-step-base";
import { PaymentMethod } from '../../payment-methods/payment-method';
import { StoreState } from '../../../store/store.interface';
import { connect } from 'pwa-helpers';
import { customElement } from 'lit/decorators.js';
import { handleApiError } from '../../../common/utils/api-utils';
import {
  isValidApplePayDetail,
  isValidGooglePayDetail,
  isValidPaypalDetail,
  isValidVenmoDetail
} from '../../../common/utils/payment-util';
import {
  calculateTax,
  getBraintreeClientTokenApiStatus,
  getBraintreeHostedFieldsStatus,
  getClientTokenResponseError,
  getPaymentDetails,
  getTaxCalculationError,
  getTaxCalculationStatus
} from '../shop-checkout';

/**
 * Checkout step to load payment options and capture payment details.
 */
@customElement('mhecomm-payment-billing-national-checkout-step')
export class PaymentBillingNationalCheckoutStep extends connect(store)(PaymentBillingCheckoutStepBase) {

  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
    super.firstUpdated(_changedProperties);
    this.focusOnSelectedOption();
  }

  stateChanged(state: StoreState) {
    this.parentStateChanged(state);

    this.canEditStep(state);
    this.handleApiFailures(state);
    this.isLoading = getBraintreeClientTokenApiStatus(state) === ActionStatus.loading
      || (this.isActive && this.availablePaymentOptions.length < 1);
    this.handleEditBillingAddress(state);
    this.showNextStepApplePayPayment = !!isValidApplePayDetail(getPaymentDetails(state));
    this.showNextStepGooglePayPayment = !!isValidGooglePayDetail(getPaymentDetails(state));
    this.showNextStepPaypalPayment = !!isValidPaypalDetail(getPaymentDetails(state));
    this.showNextStepVenmoPayment = !!isValidVenmoDetail(getPaymentDetails(state));

    if (
      getBraintreeHostedFieldsStatus(state)
      && getBraintreeHostedFieldsStatus(state) === BraintreeHostedFieldsStatus.INVALID
    ) {
      this.braintreeHostedFieldsErrorMessage = MessageTemplateId.BRAIN_TREE_HOSTED_FIELDS_ERROR;
    } else {
      this.braintreeHostedFieldsErrorMessage = undefined;
    }
    this.updateNextStepButton(state);
  }

  override handleApiFailures(state: StoreState){
    if (getTaxCalculationStatus(state) === ActionStatus.failed) {
      handleApiError(getTaxCalculationError(state));
      return;
    }
    if(getBraintreeClientTokenApiStatus(state) === ActionStatus.failed){
      handleApiError(getClientTokenResponseError(state));
      return;
    }
  }

  override loadStep(): void {
      super.loadStep();
      this.focusOnSelectedOption();
  }

  /**
   * Check if Paypal payment should be enabled.
   */
  protected override isPaypalEnabled(payload: ClientTokenDetails): boolean {
    return payload.paypalEnabled;
  }

  /**
   * On click Review Order button, validate payment method and then calculate tax.
   */
   override async stepDoValidation() {
    if (this.selectedPaymentMethodType?.component) {
      this.selectedPaymentMethodEl = this.shadowRoot!.querySelector(this.selectedPaymentMethodType?.component) as PaymentMethod;
      try {
        await this.selectedPaymentMethodEl.validatePaymentMethod();
        await store.dispatch(calculateTax()).unwrap();
      } catch(error) {
        console.error(`Failed to validate ${this.selectedPaymentMethodType.displayName}`,error);
        throw error;
      }

      const freeTrialCountryRestriction = new FreeTrialCountryRestriction();
      if (!freeTrialCountryRestriction.validate()) {
        this.isCalculatingNextStep = false;
        await freeTrialCountryRestriction.waitForModal();
      }
    }
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-payment-billing-national-checkout-step': PaymentBillingNationalCheckoutStep;
  }
}
